import { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import Touchable from "src/components/Touchable";
import Wheel from "src/components/Wheel";
import { RenderWheelItemProps, WheelProps } from "src/components/Wheel/types";
import { parseGatheringThumbnailUrl } from "src/constants/GatheringThumbnail";
import { createGatheringStore } from "src/contexts/GatheringContext/gatheringStore";
import parseGatheringPreviewTemplateUrl from "src/helpers/parseV0TemplateUrl/parseGatheringPreviewTemplateUrl";
import useStyle from "src/hooks/useStyle";
import { PaletteOption, ReactionOption } from "src/screens/(internal)/CustomThemeModal/types";
import { color } from "src/styles";
import { Image, Text } from "src/swsh-native";
import { useStore } from "zustand";

const renderWheelItem: WheelProps<any>["renderItem"] = ({
	item,
	width,
	height,
	borderRadius,
}: RenderWheelItemProps<{ readUrl: { v0TemplateUrl?: string | null } }>) => {
	const url = parseGatheringPreviewTemplateUrl({ readUrl: item?.readUrl });
	if (!url) return null;
	return (
		<Image
			alt="Photo"
			src={url}
			dim={{
				width,
				height,
			}}
			style={{
				borderRadius,
				overflow: "hidden",
			}}
		/>
	);
};

const WHEEL_SIZE = 240;
const GatheringThemePreviewHeaderItem = ({
	thumbnailUrl: thumbnailUrlProp,
	store,
	onThumbnailEdit,
	palette,
	reaction,
}: {
	thumbnailUrl?: string | null;
	store: ReturnType<typeof createGatheringStore>;
	onThumbnailEdit: () => void;
	palette?: PaletteOption;
	reaction?: ReactionOption;
}) => {
	const { theme: deviceTheme } = useStyle();
	const previewPhotos = useStore(store, (state) => state.previewPhotos);
	const gatheringThumbnail = useStore(store, (state) => state.thumbnail);
	const gatheringThumbnailUrl = useMemo(() => {
		return parseGatheringThumbnailUrl({
			readUrl: gatheringThumbnail?.readUrl,
		});
	}, [gatheringThumbnail]);
	const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(
		thumbnailUrlProp ?? gatheringThumbnailUrl ?? null,
	);

	useEffect(() => {
		if (gatheringThumbnailUrl) {
			setThumbnailUrl(gatheringThumbnailUrl);
		}
	}, [gatheringThumbnailUrl]);

	/**
	 * TODO: This fails when the same `thumbnailUrlProp` is passed in after
	 * the thumbnail changes in the store (i.e. through edit cover modal).
	 * This should be fixed when we add thumbnails to templates.
	 */
	useEffect(() => {
		if (thumbnailUrlProp) {
			setThumbnailUrl(thumbnailUrlProp);
		}
	}, [thumbnailUrlProp]);

	return (
		<View>
			<Wheel
				size={WHEEL_SIZE}
				autoRotateConfig={false}
				data={previewPhotos}
				renderItem={renderWheelItem}
			>
				{thumbnailUrl && (
					<>
						<Image
							testID="CustomThemeModalPreviewThumbnail"
							alt="Thumbnail preview"
							src={thumbnailUrl}
							dim="auto"
							style={{
								width: "100%",
								height: "100%",
							}}
						/>
						<Touchable
							onPress={onThumbnailEdit}
							containerStyle={{
								position: "absolute",
								width: "100%",
								height: "100%",
							}}
						/>
					</>
				)}
			</Wheel>
			<View
				testID="CustomThemeModalPreviewReaction"
				style={{
					position: "absolute",
					right: 0,
					bottom: 0,
					width: 48,
					height: 48,
					borderRadius: 24,
					alignItems: "center",
					justifyContent: "center",
					backgroundColor: palette?.primary ?? color.ButtonBackground[deviceTheme],
				}}
			>
				<Text large>{reaction}</Text>
			</View>
		</View>
	);
};

export default GatheringThemePreviewHeaderItem;
